/**
 * TransitionParametersEditor Component
 * 
 * Comprehensive editor for all transition parameters organized by category.
 * Supports both Prime (type definition) and Company (value entry) modes.
 */

import React, { useState } from 'react';
import { Collapse, Button, Space, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ParameterDefinitionEditor } from './ParameterDefinitionEditor';
import {
  TransitionParameters,
  ParameterDefinition,
  createParameterDefinition,
} from '../../types/transitionParameters';
import './TransitionParametersEditor.css';

const { Panel } = Collapse;

interface TransitionParametersEditorProps {
  parameters: TransitionParameters;
  onChange: (parameters: TransitionParameters) => void;
  mode?: 'prime' | 'company';
  disabled?: boolean;
}

export const TransitionParametersEditor: React.FC<TransitionParametersEditorProps> = ({
  parameters,
  onChange,
  mode = 'company',
  disabled = false,
}) => {
  const [activeKey, setActiveKey] = useState<string[]>(['conversion']);

  // Category definitions with tooltips
  const categories = [
    {
      key: 'demand',
      title: 'Demand Parameters',
      description: 'Target values and capacity constraints',
      fields: {
        target_value: {
          label: 'Target Value',
          tooltip: 'Explicit demand target (e.g., 500 units/day)',
          defaultDef: createParameterDefinition('number', undefined, {
            unit: 'units',
            time_period: 'day',
            description: 'Target demand value or rate',
          }),
        },
        capacity_value: {
          label: 'Capacity Value',
          tooltip: 'Maximum processing capacity',
          defaultDef: createParameterDefinition('number', undefined, {
            unit: 'units',
            time_period: 'hour',
            description: 'Maximum capacity value or rate',
          }),
        },
      },
    },
    {
      key: 'conversion',
      title: 'Conversion Parameters',
      description: 'Material quantities, yields, and conversion rates',
      fields: {
        conversion_rate: {
          label: 'Conversion Rate',
          tooltip: 'Output per input (e.g., 0.95 = 95% yield)',
          defaultDef: createParameterDefinition('number', 1.0, {
            description: 'Output quantity per input quantity',
          }),
        },
        material_quantity: {
          label: 'Material Quantity',
          tooltip: 'Materials consumed per execution (e.g., 2 wheels per bike)',
          defaultDef: createParameterDefinition('number', 1, {
            description: 'Material items consumed per transition',
          }),
        },
        resource_quantity: {
          label: 'Resource Quantity',
          tooltip: 'Resources required per execution',
          defaultDef: createParameterDefinition('number', 1, {
            description: 'Resource items required per transition',
          }),
        },
        batch_size: {
          label: 'Batch Size',
          tooltip: 'Items processed in a batch',
          defaultDef: createParameterDefinition('number', 1, {
            description: 'Number of items processed in a batch',
          }),
        },
        scrap_rate: {
          label: 'Scrap Rate',
          tooltip: 'Percentage scrapped/lost (0-1)',
          defaultDef: createParameterDefinition('number', 0, {
            description: 'Percentage of items scrapped/lost',
          }),
        },
      },
    },
    {
      key: 'timing',
      title: 'Timing Parameters',
      description: 'Durations, lead times, and cycle times',
      fields: {
        duration: {
          label: 'Duration',
          tooltip: 'Time to complete one execution',
          defaultDef: createParameterDefinition('number', undefined, {
            unit: 'minutes',
            description: 'Time to complete one transition execution',
          }),
        },
        setup_time: {
          label: 'Setup Time',
          tooltip: 'Setup time before processing',
          defaultDef: createParameterDefinition('number', 0, {
            unit: 'minutes',
            description: 'Time required for setup before processing',
          }),
        },
        lead_time: {
          label: 'Lead Time',
          tooltip: 'Total time from start to completion',
          defaultDef: createParameterDefinition('number', undefined, {
            unit: 'days',
            description: 'Total time from start to completion',
          }),
        },
        cycle_time: {
          label: 'Cycle Time',
          tooltip: 'Time between completing successive items',
          defaultDef: createParameterDefinition('number', undefined, {
            unit: 'minutes',
            description: 'Time between completing successive items',
          }),
        },
      },
    },
    {
      key: 'cost',
      title: 'Cost Parameters',
      description: 'Material, labor, and overhead costs',
      fields: {
        material_cost: {
          label: 'Material Cost',
          tooltip: 'Material cost per unit/batch',
          defaultDef: createParameterDefinition('number', undefined, {
            currency: 'USD',
            per: 'unit',
            description: 'Material cost',
          }),
        },
        labor_cost: {
          label: 'Labor Cost',
          tooltip: 'Labor cost per hour/unit',
          defaultDef: createParameterDefinition('number', undefined, {
            currency: 'USD',
            per: 'hour',
            description: 'Labor cost',
          }),
        },
        overhead_cost: {
          label: 'Overhead Cost',
          tooltip: 'Overhead cost per unit/batch',
          defaultDef: createParameterDefinition('number', undefined, {
            currency: 'USD',
            per: 'unit',
            description: 'Overhead cost',
          }),
        },
      },
    },
    {
      key: 'quality',
      title: 'Quality Parameters',
      description: 'Defect rates, rework, and inspection',
      fields: {
        defect_rate: {
          label: 'Defect Rate',
          tooltip: 'Expected defect rate (0-1)',
          defaultDef: createParameterDefinition('number', 0, {
            description: 'Expected defect rate',
          }),
        },
        rework_rate: {
          label: 'Rework Rate',
          tooltip: 'Percentage requiring rework (0-1)',
          defaultDef: createParameterDefinition('number', 0, {
            description: 'Percentage requiring rework',
          }),
        },
        inspection_required: {
          label: 'Inspection Required',
          tooltip: 'Whether inspection is required',
          defaultDef: createParameterDefinition('boolean', false, {
            description: 'Inspection required flag',
          }),
        },
      },
    },
    {
      key: 'inventory',
      title: 'Inventory Parameters',
      description: 'Buffer levels and reorder points',
      fields: {
        min_buffer: {
          label: 'Minimum Buffer',
          tooltip: 'Minimum inventory buffer (safety stock)',
          defaultDef: createParameterDefinition('number', undefined, {
            description: 'Minimum inventory buffer',
          }),
        },
        max_buffer: {
          label: 'Maximum Buffer',
          tooltip: 'Maximum inventory buffer',
          defaultDef: createParameterDefinition('number', undefined, {
            description: 'Maximum inventory buffer',
          }),
        },
        reorder_point: {
          label: 'Reorder Point',
          tooltip: 'Trigger point for replenishment',
          defaultDef: createParameterDefinition('number', undefined, {
            description: 'Trigger point for replenishment',
          }),
        },
        economic_order_quantity: {
          label: 'Economic Order Quantity',
          tooltip: 'Optimal order/batch size',
          defaultDef: createParameterDefinition('number', undefined, {
            description: 'Optimal order/batch size',
          }),
        },
      },
    },
    {
      key: 'rollup',
      title: 'Rollup/Aggregation Parameters',
      description: 'Configure aggregation of source artifact items into lane summaries',
      fields: {
        aggregation_enabled: {
          label: 'Enable Aggregation',
          tooltip: 'Enable rollup of multiple source items',
          defaultDef: createParameterDefinition('boolean', false, {
            description: 'Aggregate source items into this transition',
          }),
        },
        aggregation_period: {
          label: 'Aggregation Period',
          tooltip: 'Time period for grouping (monthly, weekly, etc.)',
          defaultDef: createParameterDefinition('string', 'monthly', {
            description: 'Period for creating aggregate lane items',
          }),
        },
        aggregation_timezone: {
          label: 'Timezone',
          tooltip: 'Timezone for period boundaries',
          defaultDef: createParameterDefinition('string', 'UTC', {
            description: 'Timezone for aggregation period boundaries',
          }),
        },
        source_artifact_type: {
          label: 'Source Artifact Type',
          tooltip: 'Type of artifact to aggregate from',
          defaultDef: createParameterDefinition('string', undefined, {
            description: 'Artifact type containing items to roll up',
          }),
        },
        source_state_filter: {
          label: 'Source State Filter',
          tooltip: 'Filter source items by state',
          defaultDef: createParameterDefinition('array', undefined, {
            description: 'States to include (empty = all states)',
          }),
        },
        selector_field: {
          label: 'Selector Field',
          tooltip: 'Field to match for grouping',
          defaultDef: createParameterDefinition('string', undefined, {
            description: 'Field containing bucket key in source items',
          }),
        },
        selector_comparison: {
          label: 'Selector Comparison',
          tooltip: 'How to match selector field',
          defaultDef: createParameterDefinition('string', 'equals', {
            description: 'Comparison operator for matching',
          }),
        },
        selector_value: {
          label: 'Selector Value Expression',
          tooltip: 'Value/expression to match',
          defaultDef: createParameterDefinition('string', '$.bucket_id', {
            description: 'Expression for matching source items',
          }),
        },
        measure_total_field: {
          label: 'Measure Field',
          tooltip: 'Field to aggregate',
          defaultDef: createParameterDefinition('string', undefined, {
            description: 'Numeric field to sum/aggregate',
          }),
        },
        measure_total_operation: {
          label: 'Measure Operation',
          tooltip: 'How to aggregate (sum, avg, etc.)',
          defaultDef: createParameterDefinition('string', 'sum', {
            description: 'Mathematical operation for aggregation',
          }),
        },
        measure_count_enabled: {
          label: 'Enable Count',
          tooltip: 'Count source records',
          defaultDef: createParameterDefinition('boolean', false, {
            description: 'Count number of source items',
          }),
        },
        measure_conditional_field: {
          label: 'Conditional Measure Field',
          tooltip: 'Field for conditional aggregation',
          defaultDef: createParameterDefinition('string', undefined, {
            description: 'Field for conditional aggregation',
          }),
        },
        measure_conditional_operation: {
          label: 'Conditional Operation',
          tooltip: 'sum_if or count_if',
          defaultDef: createParameterDefinition('string', 'sum_if', {
            description: 'Conditional aggregation operation',
          }),
        },
        measure_conditional_filter: {
          label: 'Conditional Filter',
          tooltip: 'Boolean filter expression',
          defaultDef: createParameterDefinition('string', undefined, {
            description: 'Filter condition for conditional measure',
          }),
        },
        bucket_key_format: {
          label: 'Bucket Key Format',
          tooltip: 'Date format for bucket keys (e.g., YYYY-MM)',
          defaultDef: createParameterDefinition('string', 'YYYY-MM', {
            description: 'Format string for bucket identifiers',
          }),
        },
        bucket_key_field: {
          label: 'Bucket Key Field',
          tooltip: 'Field storing bucket key',
          defaultDef: createParameterDefinition('string', 'bucket_id', {
            description: 'Field name for bucket identifier',
          }),
        },
        idempotency_enabled: {
          label: 'Enable Idempotency',
          tooltip: 'Prevent duplicate processing',
          defaultDef: createParameterDefinition('boolean', false, {
            description: 'Ensure each source event processed once',
          }),
        },
        idempotency_event_id_field: {
          label: 'Event ID Field',
          tooltip: 'Field with unique event ID',
          defaultDef: createParameterDefinition('string', undefined, {
            description: 'Field containing unique event identifier',
          }),
        },
        idempotency_watermark_field: {
          label: 'Watermark Field',
          tooltip: 'Last processed event sequence',
          defaultDef: createParameterDefinition('string', undefined, {
            description: 'Field tracking last processed sequence',
          }),
        },
        window_start_field: {
          label: 'Window Start Field',
          tooltip: 'Period start timestamp field',
          defaultDef: createParameterDefinition('string', 'bucket_start', {
            description: 'Field defining aggregation window start',
          }),
        },
        window_end_field: {
          label: 'Window End Field',
          tooltip: 'Period end timestamp field',
          defaultDef: createParameterDefinition('string', 'bucket_end', {
            description: 'Field defining aggregation window end',
          }),
        },
        window_include_boundaries: {
          label: 'Include Boundaries',
          tooltip: 'Include items at exact window edges',
          defaultDef: createParameterDefinition('boolean', true, {
            description: 'Include timestamps at window boundaries',
          }),
        },
        target_field_source: {
          label: 'Target Field Source',
          tooltip: 'Config field with target value',
          defaultDef: createParameterDefinition('string', undefined, {
            description: 'Path to target value configuration',
          }),
        },
        target_comparison_enabled: {
          label: 'Enable Target Comparison',
          tooltip: 'Compare total to target',
          defaultDef: createParameterDefinition('boolean', false, {
            description: 'Compare rollup total to target',
          }),
        },
      },
    },
  ];

  const handleParameterChange = (
    categoryKey: string,
    fieldKey: string,
    value: ParameterDefinition
  ) => {
    const categoryName = `${categoryKey}_parameters` as keyof TransitionParameters;
    
    onChange({
      ...parameters,
      [categoryName]: {
        ...(parameters[categoryName] || {}),
        [fieldKey]: value,
      },
    });
  };

  const handleDeleteParameter = (categoryKey: string, fieldKey: string) => {
    const categoryName = `${categoryKey}_parameters` as keyof TransitionParameters;
    const categoryParams = { ...(parameters[categoryName] || {}) };
    delete (categoryParams as any)[fieldKey];
    
    onChange({
      ...parameters,
      [categoryName]: categoryParams,
    });
  };

  const renderCategoryPanel = (category: typeof categories[0]) => {
    const categoryName = `${category.key}_parameters` as keyof TransitionParameters;
    const categoryParams = parameters[categoryName] as any || {};

    return (
      <Panel
        key={category.key}
        header={
          <div className="panel-header">
            <span className="panel-title">{category.title}</span>
            <span className="panel-description">{category.description}</span>
          </div>
        }
      >
        <div className="category-parameters">
          {Object.entries(category.fields).map(([fieldKey, field]: [string, any]) => {
            const paramValue = categoryParams[fieldKey];
            
            // Show parameter if it exists OR if in prime mode (to allow adding)
            if (!paramValue && mode === 'company') {
              return null;
            }

            return (
              <div key={fieldKey} className="parameter-item">
                <ParameterDefinitionEditor
                  label={field.label}
                  parameter={paramValue || field.defaultDef}
                  onChange={(value) => handleParameterChange(category.key, fieldKey, value)}
                  mode={mode}
                  disabled={disabled}
                  tooltip={field.tooltip}
                />
                {mode === 'prime' && !disabled && (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteParameter(category.key, fieldKey)}
                    className="delete-parameter-btn"
                  />
                )}
              </div>
            );
          })}

          {/* Add Parameter Button (Prime mode only) */}
          {mode === 'prime' && !disabled && (
            <div className="add-parameter-section">
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => {
                  // Could open a dialog to select which parameter to add
                  // For now, just show a tooltip
                }}
                block
              >
                Add Custom Parameter
              </Button>
            </div>
          )}
        </div>
      </Panel>
    );
  };

  return (
    <div className="transition-parameters-editor">
      <div className="editor-header">
        <h3>
          Transition Parameters
          <Tooltip title="Define how the transition behaves in the projection engine">
            <InfoCircleOutlined style={{ marginLeft: 8, fontSize: 14, color: '#1890ff' }} />
          </Tooltip>
        </h3>
        {mode === 'prime' && (
          <span className="mode-badge prime">Prime Mode - Define Types</span>
        )}
        {mode === 'company' && (
          <span className="mode-badge company">Company Mode - Set Values</span>
        )}
      </div>

      <Collapse
        activeKey={activeKey}
        onChange={(keys) => setActiveKey(keys as string[])}
        className="parameters-collapse"
      >
        {categories.map((category) => renderCategoryPanel(category))}
      </Collapse>
    </div>
  );
};

